import React from 'react';
import { useSelector } from 'react-redux';
import { Stack, styled } from '@mui/material';
import { FormProvider } from 'react-hook-form';
import { useAssessments } from 'features/assessments/hooks/useAssessments';
import { AssessmentsProvider } from 'features/assessments/contexts/AssessmentsContext';
import { AssessmentsHeader } from 'components/AssessmentsHeader/AssessmentsHeader';
import { AssessmentsQuestion } from 'components/AssessmentsQuestion/AssessmentsQuestion';
import { AssessmentsSubmission } from 'components/AssessmentsSubmission/AssessmentsSubmission';
import { useAssessmentsContext } from 'features/assessments/hooks/useAssessmentsContext';
import { useAssessmentsSkipLogic } from 'features/assessments/hooks/useAssessmentsSkipLogic';
import { useAssessmentsAlertError } from 'features/assessments/hooks/useAssessmentsAlertError';
import { Error } from '@mui/icons-material';
import { Alert } from 'components/Alert/Alert';
import { useTimeoutHook } from 'features/assessments/hooks/useTimeoutHook';
import { AssessmentTimeoutDialog } from 'components/AssessmentTimeoutDialog/AssessmentTimeoutDialog';
import { ASSESSMENTS_TIMER_SECONDS } from 'features/assessments/constants';
import { AssessmentUserName } from 'components/AssessmentUserName/AssessmentUserName';
import {
  selectAssessmentsMode,
  selectAssessmentSource,
} from 'features/assessments/assessmentsAuthSlice';
import { useAssessmentInitialiseSkipLogic } from 'features/assessments/hooks/useAssessmentInitialiseSkipLogic';

const FormContainer = styled('form', {
  shouldForwardProp: (prop) => prop !== 'fillContainer',
})<{ fillContainer: boolean }>(({ theme, fillContainer }) => ({
  display: 'flex',
  flexDirection: 'column',
  // Inside the checklist modal the form fills the modal instead of the viewport
  height: fillContainer ? '100%' : '100dvh',
  '@supports (height: 100svh)': {
    height: fillContainer ? '100%' : '100svh',
  },
  width: fillContainer ? '100%' : '100dvw',
  overflowY: 'auto',
  backgroundColor: theme.palette.brandBackgroundPrimary.main,
}));

const QuestionsContainer = styled(Stack)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(2),
  gap: theme.spacing(2),
  maxWidth: 650,
  width: '100%',
  margin: '0 auto',
  boxSizing: 'border-box',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    gap: theme.spacing(2),
  },
}));

export const AssessmentsContent = () => {
  useAssessmentInitialiseSkipLogic();

  // Assessments opened from the checklist use the member-web session timeout instead of their own
  const isAuthenticated = useSelector(selectAssessmentsMode) === 'authenticated';
  // A source is only set by native SET_AUTH, where the app handles the session timeout
  const hasNativeSource = useSelector(selectAssessmentSource) !== null;

  const { assessmentsFormRef } = useAssessmentsContext();

  const { registerRef, jumpToQuestion } = useAssessmentsSkipLogic();

  const {
    headerProps,
    questions,
    triggerSkipLogic,
    formValues,
    submitAnswers,
    currentPage,
    pagesCount,
    firstName,
  } = useAssessments();

  const { onSubmit, isSubmitting, isSaving, isOnline, onSaveAndClose, onSaveAndCloseOnTimeout } =
    headerProps;

  // TODO: Refactor to move the whole logic into separate hook.
  const { formMethods } = useAssessmentsContext();

  const { isShowingAlertError, alertError } = useAssessmentsAlertError();

  const { message, position, handleDismiss } = alertError;
  const [isTimeoutWarning, setIsTimeoutWarning] = React.useState(false);

  const handleCloseSession = () => {
    setIsTimeoutWarning(false);
    onSaveAndCloseOnTimeout();
  };

  const handleAutoSave = () => {
    onSaveAndClose();
  };

  const { activateTimer } = useTimeoutHook({
    onWarning: () => setIsTimeoutWarning(true),
    onAutoSave: handleAutoSave,
    onClose: handleCloseSession,
    disabled: isAuthenticated || hasNativeSource,
  });

  const handleContinue = () => {
    activateTimer();
    setIsTimeoutWarning(false);
  };

  return (
    <>
      <AssessmentsProvider>
        <FormProvider {...formMethods}>
          <FormContainer ref={assessmentsFormRef} fillContainer={isAuthenticated}>
            <AssessmentsHeader {...headerProps} />
            <QuestionsContainer id="questionsContainer">
              {currentPage === 1 && <AssessmentUserName firstName={firstName} />}
              {questions?.map((question) => (
                <AssessmentsQuestion
                  key={question.id}
                  ref={registerRef(question.id)}
                  onNextClick={triggerSkipLogic}
                  {...question}
                  formValue={formValues[question.id]}
                  isLastQuestion={question.id === questions[questions.length - 1].id}
                  isLastPage={currentPage === pagesCount}
                  onSubmit={onSubmit}
                  isSubmitDisabled={isSubmitting || isSaving || !isOnline}
                />
              ))}
            </QuestionsContainer>
          </FormContainer>
        </FormProvider>
      </AssessmentsProvider>
      <AssessmentsSubmission jumpToQuestion={jumpToQuestion} submitAnswers={submitAnswers} />
      <Alert
        isPresented={isShowingAlertError}
        variant="filled"
        severity="error"
        message={message}
        icon={<Error />}
        anchorOrigin={{ vertical: position, horizontal: 'center' }}
        handleDismiss={handleDismiss}
        snackbarSx={{ position: 'fixed' }}
      />
      <AssessmentTimeoutDialog
        open={isTimeoutWarning}
        initialSeconds={ASSESSMENTS_TIMER_SECONDS}
        onContinue={handleContinue}
        onClose={handleCloseSession}
      />
    </>
  );
};
